import { IconButton, Select, Tag } from '@nike/eds';
import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { ActionMeta, SingleValue } from 'react-select';
import { selectedRewriteAtom } from '../../atoms/rewrite';

enum TagColor {
  Teal = 'teal',
  Blue = 'blue',
  Green = 'green',
  Yellow = 'yellow',
  Pink = 'pink',
  Orange = 'orange',
  Purple = 'purple',
}

export default function TagsInput({
  onRewriteTagsChange,
  resetKey,
  parentComponentName,
  rewriteTagsErrorMessage,
  setRewriteTagsErrorMessage,
}: TagsInputProps) {
  const [rewrite] = useAtom(selectedRewriteAtom);
  const [rewriteTags, setRewriteTags] = useState<RewriteTag[]>([]);
  // State to track which tag is being hovered
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    if (rewriteTagsErrorMessage !== '') {
      setRewriteTagsErrorMessage(rewriteTagsErrorMessage);
    } else {
      // Clear the error message if the condition no longer applies
      setRewriteTagsErrorMessage('');
    }
  }, [rewriteTagsErrorMessage, setRewriteTagsErrorMessage]);

  // Call this function whenever rewriteTags is updated
  useEffect(() => {
    if (onRewriteTagsChange) {
      onRewriteTagsChange(rewriteTags);
    }
  }, [rewriteTags]);

  useEffect(() => {
    if (parentComponentName === 'termItemEdit') {
      const newRewriteTags = rewrite?.rewriteTags || [];
      // Update local state with the new value
      setRewriteTags(newRewriteTags);
      setRewriteTagsErrorMessage('');
    } else if (parentComponentName === 'termItemCreate') {
      setRewriteTags([{ tagName: 'normal', tagColor: 'orange' }]);
    }
  }, [parentComponentName, rewrite?.rewriteTags, resetKey]);

  // Define an array of possible colors for the tags with the type TagColor
  const tagColors: TagColor[] = Object.values(TagColor);

  // Function to get a random color from the tagColors array
  const getRandomColor = (): TagColor => {
    return tagColors[Math.floor(Math.random() * tagColors.length)];
  };

  const handleSelectChange = (
    newValue: SingleValue<{ value: string; label: string }>,
    actionMeta: ActionMeta<{ value: string; label: string }>,
  ) => {
    console.log(actionMeta);

    const selectValue = newValue?.value || '';
    if (!rewriteTags.some((tag) => tag.tagName === selectValue)) {
      setRewriteTagsErrorMessage('');
      setRewriteTags([
        ...rewriteTags,
        { tagName: selectValue, tagColor: getRandomColor() },
      ]);
      onRewriteTagsChange?.([
        ...rewriteTags,
        { tagName: selectValue, tagColor: getRandomColor() },
      ]);
    }
  };

  const removeTag = (indexToRemove: number) => {
    const newRewriteTags = rewriteTags.filter(
      (_, index) => index !== indexToRemove,
    );
    setRewriteTags(newRewriteTags);
    onRewriteTagsChange?.(newRewriteTags);
    setRewriteTagsErrorMessage('');
  };

  const options = [
    { value: 'normal', label: 'Normal' },
    { value: 'typo', label: 'Typo' },
  ];

  return (
    <div className='tw-mb-10 tw-pb-20'>
      <Select
        id='rewrite-tags-dropdown'
        options={options}
        onChange={handleSelectChange}
        isMulti={false}
        defaultValue={{ value: 'normal', label: 'Normal' }}
        label=''
        subtitle='Rewrite Tags Selection:'
        hasErrors={rewriteTagsErrorMessage !== ''}
        errorMessage={rewriteTagsErrorMessage}
      />

      <div className='tw-mt-4'>
        {rewriteTags.map((tag, index) => (
          <div
            key={index}
            className='eds-tags-input-container tw-inline-flex tw-items-center tw-mr-2 tw-mb-2'
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <Tag color={tag.tagColor} size='medium'>
              {tag.tagName}
            </Tag>
            {hoveredIndex === index && (
              <IconButton
                label=''
                size='small'
                variant='ghost'
                onClick={() => removeTag(index)}
                icon='Close'
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
